import { CommonConstants } from '../constants/common-constants';
import { HTTPService } from './httpService';

const api = new HTTPService();
const base = CommonConstants.BaseAddress;

export type QuestionType = 0 | 1 | 2;  // 0=MCQ  1=MultiSelect  2=TrueFalse

export interface ExamOption {
  optionId?: number;
  optionLabel: string;
  optionText: string | null;
  optionImage: string | null;
  isCorrect: boolean;
}

export interface ExamQuestion {
  questionId: number;
  questionNo: number;
  questionText: string;         // HTML / LaTeX
  questionImage: string | null;
  marks: number;
  negativeMarks: number;
  questionType: QuestionType;
  options: ExamOption[];
}

export interface ExamPaper {
  examPaperId: number;
  title: string;
  description: string | null;
  examName: string;
  subject: string | null;
  durationMins: number;
  totalMarks: number;
  status: 0 | 1;               // 0=Draft  1=Released
  statusLabel: string;
  createdOn: string;
  questionCount: number;
  questions: ExamQuestion[];
}

export type SavePaperPayload = Pick<ExamPaper, 'title' | 'description' | 'examName' | 'subject' | 'durationMins'>;

export const examPaperService = {
  getAll: async (): Promise<ExamPaper[]> => {
    const res = await api.get(`${base}${CommonConstants.ApiConstants.ExamPapers}`);
    return res.data?.data ?? [];
  },

  getById: async (id: number): Promise<ExamPaper> => {
    const res = await api.get(`${base}${CommonConstants.ApiConstants.ExamPaper(id)}`);
    return res.data?.data;
  },

  create: async (payload: SavePaperPayload): Promise<ExamPaper> => {
    const res = await api.post(`${base}${CommonConstants.ApiConstants.ExamPapers}`, payload);
    return res.data?.data;
  },

  update: async (id: number, payload: SavePaperPayload): Promise<ExamPaper> => {
    const res = await api.put(`${base}${CommonConstants.ApiConstants.ExamPaper(id)}`, payload);
    return res.data?.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`${base}${CommonConstants.ApiConstants.ExamPaper(id)}`);
  },

  release: async (id: number): Promise<void> => {
    await api.post(`${base}${CommonConstants.ApiConstants.ExamPaperRelease(id)}`);
  },

  retract: async (id: number): Promise<void> => {
    await api.post(`${base}${CommonConstants.ApiConstants.ExamPaperRetract(id)}`);
  },

  saveQuestions: async (
    id: number,
    questions: Omit<ExamQuestion, 'questionId'>[]
  ): Promise<ExamPaper> => {
    const res = await api.put(
      `${base}${CommonConstants.ApiConstants.ExamPaperQuestions(id)}`,
      { questions }
    );
    return res.data?.data;
  },

  uploadImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.fileUpload(
      `${base}${CommonConstants.ApiConstants.ExamPaperImageUpload}`,
      formData
    );
    return res.data?.data as string;
  },
};
